import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, Skull, Trophy, Flame, ChevronLeft } from "lucide-react";
import { absUrl } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORTS = [
  { key: "floors_cleared", label: "FLOORS" },
  { key: "final_level",    label: "LEVEL" },
  { key: "final_xp",       label: "XP" },
  { key: "gold",           label: "GOLD" },
];
const OUTCOMES = [
  { key: "all",     label: "ALL" },
  { key: "death",   label: "DEATHS" },
  { key: "victory", label: "VICTORIES" },
];

export default function HallOfAshes() {
  const [sort, setSort] = useState("floors_cleared");
  const [outcome, setOutcome] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hall`, { params: { sort, outcome, limit: 30 } });
      setData(r.data);
    } catch {
      setData({ entries: [], count: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, outcome]);

  return (
    <div className="min-h-screen px-4 md:px-8 lg:px-12 py-10" data-testid="hall-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="ash-text hover:text-white flex items-center gap-1 text-xs tracking-widest">
            <ChevronLeft size={14} /> REALM
          </Link>
          <div className="text-center">
            <div className="text-[10px] tracking-[0.4em] gold-text">A HALL OF FALLEN AND VICTORIOUS</div>
            <h1 className="font-heading text-4xl md:text-5xl text-[#f0e6d2] italic mt-1">The Hall of <span className="gold-text">Ashes</span></h1>
          </div>
          <div className="w-16" />
        </div>

        <div className="panel p-4 mb-6 flex flex-wrap gap-3 items-center justify-between" data-testid="hall-filters">
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-[0.3em] ash-text mr-1">SORT</span>
            {SORTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                data-testid={`hall-sort-${s.key}`}
                className={`px-3 py-1.5 text-[10px] tracking-[0.3em] border ${sort===s.key ? "border-[#d4af37] text-[#f0e6d2]" : "border-white/10 ash-text hover:text-white"}`}
              >{s.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-[0.3em] ash-text mr-1">OUTCOME</span>
            {OUTCOMES.map((o) => (
              <button
                key={o.key}
                onClick={() => setOutcome(o.key)}
                data-testid={`hall-outcome-${o.key}`}
                className={`px-3 py-1.5 text-[10px] tracking-[0.3em] border ${outcome===o.key ? "border-[#d4af37] text-[#f0e6d2]" : "border-white/10 ash-text hover:text-white"}`}
              >{o.label}</button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="panel p-8 text-center ash-text italic">Reading names from the ash...</div>
        ) : data.entries.length === 0 ? (
          <div className="panel p-10 text-center" data-testid="hall-empty">
            <Flame size={20} className="mx-auto ember" />
            <div className="mt-3 font-heading text-2xl text-[#f0e6d2] italic">No embers have fallen yet.</div>
            <div className="ash-text text-sm mt-2">Be the first to descend.</div>
            <Link to="/" className="btn-primary inline-block mt-4">▶ ENTER THE DUNGEON</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hall-list">
            {data.entries.map((e, i) => {
              const isVic = e.outcome === "victory";
              return (
                <Link
                  to={`/card/${e.share_id}`}
                  key={e.share_id || i}
                  data-testid={`hall-entry-${e.share_id}`}
                  className="panel p-4 flex items-center gap-4 hover:border-[#d4af37] transition-colors"
                >
                  <div className="text-[10px] tracking-widest ash-text w-6 text-right">{i + 1}</div>
                  <div className="w-16 h-20 bg-black flex-shrink-0 overflow-hidden">
                    {e.portrait_url ? (
                      <img src={absUrl(e.portrait_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full skeleton" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg text-[#f0e6d2] truncate">{e.user_name || "Adventurer"}</span>
                      {isVic ? (
                        <span className="text-[9px] tracking-widest gold-text flex items-center gap-1"><Crown size={10}/>VICTOR</span>
                      ) : (
                        <span className="text-[9px] tracking-widest blood-text flex items-center gap-1"><Skull size={10}/>FALLEN</span>
                      )}
                    </div>
                    <div className="text-xs ash-text">{e.class_name} · Ch. {e.chapter || "?"} {e.chapter_title || ""}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs">
                      <span className="arcane-text">FLOOR {e.floors_cleared}</span>
                      <span className="gold-text">LVL {e.final_level}</span>
                      <span className="ash-text">XP {e.final_xp || 0}</span>
                    </div>
                  </div>
                  <Trophy size={16} className="gold-text opacity-60" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
